/**
 * Sends a signed-in customer who opens an Easy Quote link (`/easy-quote/?quoteId=<id>`) straight
 * to the quote in the store, skipping the guest step. Its only caller is
 * `pages/easy-quote/index.tsx`, which renders nothing while it returns true.
 */
import { useApplication } from "@ryerson/frontend.application";

/**
 * When the user is signed in and `quoteId` is set, sets `window.location.href` to
 * `/store/quotes/<quoteId>`, a full page load served by web-store-ui. The store has no `/quotes`
 * page file: `web-store-ui/application/src/components/NextLayouts/DynamicPathComponent.tsx`
 * matches `quotes/<id>` at the end of the path and opens the same quote details as
 * `/store/dashboard/quotes/<id>`, behind the store's `ProtectedRoute`.
 *
 * It runs during render, on every render while both hold. `authSession` is read but not used:
 * a signed-in user is redirected whether or not an Easy Quote guest session is present.
 * @param quoteId - The quote ID to redirect to
 * @returns {boolean} - true if user should be redirected, false otherwise
 */
const useRedirectIfLoggedInEasyQuote = (quoteId: string): boolean => {
	const {
		user: { isLoggedIn },
		authSession,
	} = useApplication();

	// Signed in and a quote id in the URL; `authSession` plays no part.
	if (isLoggedIn && quoteId) {
		const destination = `/store/quotes/${quoteId}`;
		// A full page load, so CloudFront serves the store app for `/store/...`.
		window.location.href = destination;
		return true;
	}

	return false;
};

export default useRedirectIfLoggedInEasyQuote;
